import { useEffect, useRef } from 'react';
import { Menu, MessageSquare, Plus } from 'lucide-react';
import { Button } from '@/components/ui';
import { ChatSidebar } from './chat/ChatSidebar';
import { AgentModelPicker } from './chat/AgentModelPicker';
import { ChatMessage, PendingProgress, ThinkingIndicator } from './chat/ChatMessage';
import { ChatComposer } from './chat/ChatComposer';
import { useAgentChat } from './chat/useAgentChat';

const EXAMPLE_QUESTIONS = [
  'Wat is de totale omzet?',
  'Hoeveel medewerkers hebben we?',
  'Toon de top 10 klanten',
];

export default function AgentChat() {
  const chat = useAgentChat();
  const {
    user,
    messages,
    isLoading,
    selectedAgent,
    sidebarOpen,
    setSidebarOpen,
  } = chat;
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const pendingMsg = isLoading ? messages.find((m) => m.pending) : undefined;

  return (
    <div className="flex h-full min-h-0 bg-background">
      <ChatSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        showSessions={Boolean(user)}
        sessions={chat.chatSessions}
        currentSessionId={chat.currentSessionId}
        onSelect={chat.loadSession}
        onNewChat={chat.startNewChat}
        onDelete={chat.deleteSession}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Header: mobile menu + agent/model selection */}
        <div className="shrink-0 border-b border-border bg-card px-4 py-3 sm:px-6">
          <div className="mb-2 flex items-center gap-2 lg:hidden">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setSidebarOpen(true)} aria-label="Gesprekken tonen">
              <Menu className="h-4 w-4" />
            </Button>
            <span className="font-display text-sm font-semibold">Chat</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={chat.startNewChat}>
              <Plus className="h-4 w-4" />
              Nieuw
            </Button>
          </div>
          <div className="mx-auto max-w-3xl">
            <AgentModelPicker
              agents={chat.agents}
              models={chat.models}
              selectedAgent={selectedAgent}
              selectedModel={chat.selectedModel}
              onAgentChange={chat.setSelectedAgent}
              onModelChange={chat.setSelectedModel}
              loadingAgents={chat.loadingAgents}
              loadingModels={chat.loadingModels}
              disabled={isLoading}
              sessionActive={Boolean(chat.sessionId)}
            />
          </div>
        </div>

        {/* Messages */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {messages.length === 0 ? (
            <div className="flex h-full items-center justify-center p-6">
              <div className="w-full max-w-xl text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-sl-yellow text-sl-charcoal">
                  <MessageSquare className="h-7 w-7" />
                </div>
                <h2 className="text-xl font-semibold">Stel een vraag</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Krijg antwoord uit het datawarehouse in gewone taal. De agent schrijft en draait de SQL voor je.
                </p>
                <div className="mt-6 grid gap-2 text-left">
                  {EXAMPLE_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => chat.setInputValue(q)}
                      disabled={isLoading || !selectedAgent}
                      className="rounded-md border border-border bg-card px-4 py-3 text-sm text-foreground shadow-card transition-colors hover:border-sl-yellow hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl space-y-6 px-4 py-6 sm:px-6">
              {messages
                .filter((message) => !message.pending) // Pending messages are shown as progress + thinking indicator
                .map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}

              {pendingMsg?.content && pendingMsg.content.length > 0 && <PendingProgress content={pendingMsg.content} />}

              {isLoading && <ThinkingIndicator stage={chat.thinkingStage} elapsed={chat.elapsedTime} />}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <ChatComposer
          value={chat.inputValue}
          onChange={chat.setInputValue}
          onSend={chat.sendMessage}
          isLoading={isLoading}
          hasAgent={Boolean(selectedAgent)}
        />
      </div>
    </div>
  );
}
